import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { type ApiOptions, api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { queryClient } from "./queryClient";

const SESSION_OPTIONS = {
  excludeToken: true,
  excludeCurrentOrganisationId: true,
  credentials: "include",
} satisfies Partial<ApiOptions>;
const CSRF_TOKEN_HEADER_KEY = "X-XSRF-TOKEN";

export const inviteSessionEndpoint = (invitationId: string) =>
  `${FORMS_API_BASE_ENDPOINT}/assistant-invitations/${invitationId}/session`;

const keys = {
  csrfToken: (invitationId: string) => ["assistantInviteSession", invitationId, "csrfToken"] as const,
} as const;

const csrfTokenQueryOptions = (invitationId: string) =>
  queryOptions({
    queryKey: keys.csrfToken(invitationId),
    queryFn: () =>
      api.get(`${inviteSessionEndpoint(invitationId)}/csrf-token`, {
        ...SESSION_OPTIONS,
        schema: z.object({ token: z.string() }),
      }),
    throwOnError: true,
    staleTime: Infinity,
  });

export const forgetInviteSessionCsrfToken = (invitationId: string) =>
  queryClient.removeQueries({ queryKey: keys.csrfToken(invitationId) });

export const inviteSessionRequestOptions = async (invitationId: string) => {
  const { token } = await queryClient.ensureQueryData(csrfTokenQueryOptions(invitationId));
  return { ...SESSION_OPTIONS, headers: { [CSRF_TOKEN_HEADER_KEY]: token } };
};

export const inviteSessionApi = (invitationId: string) => ({
  get: <Schema extends z.ZodTypeAny>(path: string, options?: ApiOptions<Schema>) =>
    api.get(`${inviteSessionEndpoint(invitationId)}/${path}`, { ...options, ...SESSION_OPTIONS }),
  post: async <Schema extends z.ZodTypeAny>(path: string, payload: unknown, options?: ApiOptions<Schema>) => {
    const sessionOptions = await inviteSessionRequestOptions(invitationId);
    return api.post(`${inviteSessionEndpoint(invitationId)}/${path}`, payload, {
      ...options,
      ...sessionOptions,
      headers: { ...options?.headers, ...sessionOptions.headers },
    });
  },
});
